<template>
  <div id="client-dashboard">
    <div v-if="loading" class="fill-row-loading cp-state">
      <feather-icon icon="LoaderIcon" svgClasses="h-6 w-6" class="cp-spin" />
      <div>{{ t('loading') }}</div>
    </div>
    <div v-else-if="loadError" class="cp-state">
      <vs-alert color="danger" active="true">{{ loadError }}</vs-alert>
      <vs-button size="small" class="mt-4" @click="loadAll">{{ t('retry') }}</vs-button>
    </div>

    <!-- Même trame que le tableau de bord de l'ERP (views/DashboardAnalytics.vue) :
         colonne gauche 7/12 = commandes en cours + Destination ; colonne droite
         5/12 = cartes d'état, liste, carte à onglets. -->
    <template v-else>
    <div class="vx-row mt-4 ml-0 mr-4 mb-4" style="text-align: right"></div>
    <div class="vx-row">
      <div class="vx-col lg:w-7/12 w-full">
        <vx-card :title="titleLeft" class="mb-base">
          <template slot="actions">
            <vs-button size="small" type="border" color="primary" icon-pack="feather" icon="icon-list" :title="t('see_all')" @click="go('client-quotations')" />
          </template>
          <div slot="no-body" class="mt-4 ml-4 mr-4 mb-4" v-if="current.length > 0">
            <vs-table :data="current" class="table-dark-inverted" search :max-items="1000">
              <template slot-scope="{ data }">
                <vs-tr :data="tr" :key="indextr" v-for="(tr, indextr) in data">
                  <vs-td>
                    <vs-chip class="ag-grid-cell-chips" :color="statusChipColor(tr)">
                      <span><small>{{ indextr + 1 }}.&nbsp;</small><b>
                          <router-link :to="orderRoute(tr.id)" class="text-inherit" style="text-decoration: underline;">#{{ tr.id }}</router-link> ⟶
                        </b>
                        <span v-if="loadingDate(tr)">{{ t('leaving_teiu', { date: date(loadingDate(tr)) }) }}</span>
                        <span v-else-if="tr.date_delivery_desired">{{ date(tr.date_delivery_desired) }}</span>
                        <span v-else>{{ t('not_planned') }}</span>
                        ⟶
                        <big><b>{{ volume(tr.totals.volume) }}</b><small>&nbsp;m3</small></big>&nbsp;<small>({{ tr.availability === 'to_prepare' ? t('to_prepare') : t('ready') }})</small></span>
                    </vs-chip>
                    &nbsp;
                    <vs-chip class="ag-grid-cell-chips" color="">
                      <img height="20px" :src="flagSrc(tr.delivery && tr.delivery.country_code)" :alt="tr.delivery && tr.delivery.country_code" />&nbsp;&nbsp;
                      {{ tr.company }} &nbsp;
                      <font v-if="tr.delivery && tr.delivery.place">({{ tr.delivery.place }} {{ tr.delivery.postal_code }})</font>
                      <font v-else>({{ t('no_delivery_address') }})</font>
                    </vs-chip>
                    <vs-chip v-if="loadingDate(tr)" class="ag-grid-cell-chips" :color="tr.transport.truck_plate ? '#A9CAB7' : '#c2d1f0'">
                      <span>#{{ tr.transport.id }}</span>
                      &nbsp;
                      <feather-icon icon="TruckIcon" svgClasses="h-5 w-5" />
                    </vs-chip>
                    <br />
                  </vs-td>
                  <vs-td style="width: 10px">
                    <feather-icon icon="ChevronRightIcon" svgClasses="h-5 w-5" class="cursor-pointer" @click="openOrder(tr.id)" />
                  </vs-td>
                </vs-tr>
              </template>
            </vs-table>
          </div>
          <div v-else slot="no-body" class="mt-4 ml-4 mr-4 mb-4">
            <p class="text-center p-6" style="color: #888;">{{ t('no_current_orders') }}</p>
          </div>
        </vx-card>

        <!-- Carte open source (OpenFreeMap), affichée d'emblée : usine → livraisons. -->
        <vx-card :title="t('destination')" class="mb-base">
          <div slot="no-body" class="mt-2 ml-4 mr-4 mb-4">
            <client-delivery-map :orders="mapOrders" @open="openOrder" />
          </div>
        </vx-card>
      </div>

      <div class="vx-col lg:w-5/12 w-full">
        <!-- Liste sobre, comme « Live attendance » -->
        <vx-card :title="t('latest_invoices')" class="mb-base">
          <div slot="no-body" class="mt-0 ml-4 mr-4 mb-4">
            <div v-if="invoices.length > 0">
              <div v-for="inv in invoices.slice(0, 6)" :key="inv.id" class="flex items-center justify-between py-2"
                style="border-bottom: 1px solid #eee; cursor: pointer;" @click="go('client-invoice', inv.id)">
                <span>
                  <feather-icon icon="FileTextIcon" svgClasses="h-4 w-4 text-success" class="mr-2" />
                  <b>{{ inv.invoice_no }}</b>
                </span>
                <small style="color: #888;">{{ date(inv.date) }} · {{ amount(inv.totals.ttc, inv.totals.currency) }}</small>
              </div>
            </div>
            <p v-else class="py-2" style="color: #888;">{{ t('no_invoices') }}</p>
            <div class="mt-3" style="text-align: right;">
              <small class="cursor-pointer" style="color: #b8b8b8;" @click="go('client-invoices')">{{ t('see_all') }}</small>
            </div>
          </div>
        </vx-card>

        <!-- Carte à onglets, comme « Future departures » -->
        <vx-card class="mb-8">
          <vs-tabs>
            <vs-tab :label="t('upcoming_departures')" icon-pack="feather" icon="icon-truck">
              <div class="mt-4">
                <vs-table v-if="upcoming.length" :data="upcoming">
                  <template slot-scope="{ data }">
                    <tbody>
                      <vs-tr :key="d.id" v-for="d in data">
                        <vs-td>
                          <vs-chip class="ag-grid-cell-chips" color="#AAA">
                            <span>{{ d.id }}</span>
                            &nbsp;
                            <feather-icon icon="TruckIcon" svgClasses="h-5 w-5" />
                          </vs-chip>
                        </vs-td>
                        <vs-td>
                          <small :style="{ verticalAlign: 'top' }">{{ date(d.loading_date) }}</small>
                          <div v-if="d.truck_plate"><small style="color: #888;">{{ d.truck_plate }}</small></div>
                        </vs-td>
                        <vs-td>
                          <vs-chip class="ag-grid-cell-chips" color="">
                            <img height="20px" :src="flagSrc(d.country_code)" :alt="d.country_code" />
                            &nbsp;{{ d.place || d.company }}
                          </vs-chip>
                        </vs-td>
                        <vs-td>
                          <feather-icon icon="PackageIcon" svgClasses="h-5 w-5" :style="{ cursor: 'pointer' }" @click="openOrder(d.orders[0])" />
                        </vs-td>
                      </vs-tr>
                    </tbody>
                  </template>
                </vs-table>
                <p v-else class="p-4" style="color: #888;">{{ t('no_upcoming') }}</p>
              </div>
            </vs-tab>
            <vs-tab :label="t('products_to_prepare')" icon-pack="feather" icon="icon-package">
              <div class="mt-4" v-if="lines.length > 0">
                <vs-table :data="lines" search :max-items="1000">
                  <template slot="thead">
                    <vs-th>{{ t('product') }}</vs-th>
                    <vs-th sort>{{ t('requested_delivery') }}</vs-th>
                    <vs-th :style="{ textAlign: 'right' }">{{ t('quantity') }}</vs-th>
                    <vs-th :style="{ textAlign: 'right' }">{{ t('availability') }}</vs-th>
                  </template>
                  <template slot-scope="{ data }">
                    <tbody>
                      <vs-tr :key="i" v-for="(l, i) in data">
                        <vs-td>
                          <span :style="{ fontSize: '75%' }">
                            <router-link :to="orderRoute(l.idorder)" class="text-inherit"><b>#{{ l.idorder }}</b><br />{{ l.description }}</router-link>
                          </span>
                        </vs-td>
                        <vs-td :style="{ fontSize: '75%' }">{{ date(l.date_delivery_desired) || '—' }}</vs-td>
                        <vs-td :style="{ textAlign: 'right', fontSize: '85%' }">{{ l.quantity }}</vs-td>
                        <vs-td :style="{ textAlign: 'right', float: 'right' }">
                          <vs-chip class="ag-grid-cell-chips" :color="l.stock === 'in_stock' ? 'success' : 'warning'">
                            {{ l.stock === 'in_stock' ? t('in_stock') : t('to_prepare') }}
                          </vs-chip>
                        </vs-td>
                      </vs-tr>
                    </tbody>
                  </template>
                </vs-table>
              </div>
              <p v-else class="p-4" style="color: #888;">{{ t('no_current_orders') }}</p>
            </vs-tab>
            <vs-tab :label="t('on_the_road')" icon-pack="feather" icon="icon-navigation">
              <div class="mt-4">
                <div v-if="onTheRoad.length">
                  <div v-for="d in onTheRoad" :key="d.id" class="cp-road">
                    <div class="flex items-center justify-between">
                      <vs-chip class="ag-grid-cell-chips" color="#A9CAB7">
                        <span>{{ d.id }}</span>&nbsp;<feather-icon icon="TruckIcon" svgClasses="h-5 w-5" />
                      </vs-chip>
                      <vs-chip class="ag-grid-cell-chips" color="">
                        <img height="20px" :src="flagSrc(d.country_code)" :alt="d.country_code" />&nbsp;{{ d.place || d.company }}
                      </vs-chip>
                    </div>
                    <div class="flex items-center justify-between mt-2">
                      <small>{{ date(d.loading_date) }}</small>
                      <small v-if="d.delivery_date"><b>{{ t('estimated_delivery') }} : {{ date(d.delivery_date) }}</b></small>
                    </div>
                    <vs-progress :percent="d.progress" color="success" class="mt-1" />
                    <div class="flex items-center justify-between">
                      <small style="color: #888;">
                        <template v-if="d.truck_plate">{{ d.truck_plate }}<template v-if="d.trailer_plate"> / {{ d.trailer_plate }}</template></template>
                      </small>
                      <small>
                        <span v-for="id in d.orders" :key="id" class="cp-link ml-2" @click="openOrder(id)">#{{ id }}</span>
                      </small>
                    </div>
                  </div>
                </div>
                <p v-else class="p-4" style="color: #888;">{{ t('no_on_the_road') }}</p>
              </div>
            </vs-tab>
          </vs-tabs>
        </vx-card>
      </div>
    </div>
    </template>
  </div>
</template>

<script>
// Portail client → tableau de bord (page d'arrivée). Calqué sur le tableau de
// bord de l'ERP (views/DashboardAnalytics.vue), carte pour carte : commandes en
// cours en table à pastilles (prête / à préparer + m³, client et lieu, camion),
// Destination (carte open source affichée d'emblée), liste des dernières
// factures, carte à onglets (prochains départs, produits à préparer, sur la route).
// Jamais de quantité de stock : un signe par commande / produit.
import portalPage from '@/views/client/portalPage.js'
import { portalGet } from '@/views/client/portalApi.js'
import ClientDeliveryMap from '@/views/client/ClientDeliveryMap.vue'

const FLAGS = require.context('@/assets/images/flags', false, /\.png$/)

export default {
  name: 'ClientDashboard',
  components: { ClientDeliveryMap },
  mixins: [portalPage],
  data () {
    return { all: [], invoices: [], lines: [] }
  },
  computed: {
    // Même forme que l'ERP (« Orders in progress ⟶ x m3 / y m3 ordered (z%) ») : x = volume
    // des commandes prêtes du client, pas une quantité de stock.
    titleLeft () {
      const total = this.current.reduce((s, o) => s + (o.totals.volume || 0), 0)
      const ready = this.current.filter(o => o.availability !== 'to_prepare').reduce((s, o) => s + (o.totals.volume || 0), 0)
      const pct = total ? Math.round((ready / total) * 100) : 0
      return `${this.t('nav_orders')} ⟶ ${this.volume(ready)} m3 / ${this.volume(total)} m3 ${this.t('ordered')} (${pct}%)`
    },
    // Commandes en cours, par la date affichée : départ de Teiu dès qu'un transport
    // est daté, sinon date de livraison souhaitée.
    current () {
      const key = o => `${this.loadingDate(o) || o.date_delivery_desired || o.date || ''}#${String(o.id).padStart(10, '0')}`
      return this.all.filter(o => o.status === 'in_progress').sort((a, b) => key(a).localeCompare(key(b)))
    },
    today () {
      const d = new Date()
      return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
    },
    // Un camion par transport, toutes commandes confondues : une commande est
    // facturée au chargement, le camion en route porte donc des commandes facturées.
    transports () {
      const map = new Map()
      for (const o of this.all) {
        const tr = o.transport
        if (!tr || !tr.id || !tr.loading_date) continue
        if (!map.has(tr.id)) map.set(tr.id, Object.assign({}, tr, { orders: [], company: o.company, place: null, country_code: null }))
        const entry = map.get(tr.id)
        entry.orders.push(o.id)
        // Destination : la première commande du camion qui a une adresse de livraison.
        if (!entry.place && o.delivery && o.delivery.place) {
          entry.place = o.delivery.place
          entry.country_code = o.delivery.country_code
        }
      }
      return Array.from(map.values())
    },
    // Chargement à venir (pas encore parti).
    upcoming () {
      return this.transports
        .filter(d => d.loading_date > this.today)
        .sort((a, b) => a.loading_date.localeCompare(b.loading_date))
    },
    // Chargé et pas encore arrivé : livraison estimée à venir (sans date estimée : 7 jours).
    onTheRoad () {
      const week = new Date(Date.now() - (7 * 86400000)).toISOString().slice(0, 10)
      return this.transports
        .filter(d => d.loading_date <= this.today && (d.delivery_date ? this.today <= d.delivery_date : d.loading_date >= week))
        .map(d => Object.assign({}, d, { progress: this.progressOf(d) }))
        .sort((a, b) => b.loading_date.localeCompare(a.loading_date))
    },
    // Carte : commandes en cours (dans l'ordre de la liste, pour que les numéros des
    // pastilles soient ceux de la table) puis celles dont le camion est sur la route.
    mapOrders () {
      const ids = new Set()
      for (const d of this.onTheRoad) d.orders.forEach(id => ids.add(id))
      return this.current.concat(this.all.filter(o => o.status !== 'in_progress' && ids.has(o.id)))
    }
  },
  methods: {
    // Couleurs de l'ERP : vert = prête, rouge = à préparer, gris = en attente de validation.
    statusChipColor (o) {
      if (o.awaiting_confirmation) return '#bcbcbc'
      return o.availability === 'to_prepare' ? 'danger' : 'success'
    },
    loadingDate (o) {
      return (o.transport && o.transport.loading_date) || null
    },
    // Avancement du trajet (temps écoulé entre chargement et livraison estimée).
    progressOf (d) {
      if (!d.delivery_date) return 50
      const a = new Date(`${d.loading_date}T08:00:00`).getTime()
      const b = new Date(`${d.delivery_date}T18:00:00`).getTime()
      if (b <= a) return 100
      return Math.max(5, Math.min(100, Math.round(((Date.now() - a) / (b - a)) * 100)))
    },
    volume (v) {
      return (Number(v) || 0).toFixed(2)
    },
    flagSrc (cc) {
      const code = String(cc || '').toUpperCase() === 'GB' ? 'UK' : String(cc || '').toUpperCase()
      try { return FLAGS(`./${code}.png`) } catch (e) { return FLAGS('./UE.png') }
    },
    orderRoute (id) {
      return { name: 'client-order', params: { id }, query: this.asQuery }
    },
    openOrder (id) {
      this.open('client-order', id)
    },
    go (name, id) {
      this.$router.push({ name, params: id ? { id } : {}, query: this.asQuery }).catch(() => {})
    },
    async loadAll () {
      this.loading = true
      this.loadError = null
      const as = this.portal.as
      // Une liste en échec n'empêche pas les autres de s'afficher.
      const [all, invoices, lines] = await Promise.all([
        portalGet('/orders/all', as).catch(() => null),
        portalGet('/invoices', as).catch(() => null),
        portalGet('/orders/current/lines', as).catch(() => null)
      ])
      if (!all && !invoices) this.loadError = this.t('load_error')
      this.all = (all && all.orders) || []
      this.invoices = (invoices && invoices.invoices) || []
      this.lines = (lines && lines.lines) || []
      this.loading = false
    }
  },
  created () {
    this.loadAll()
  }
}
</script>

<style lang="scss">
/* Mêmes règles que DashboardAnalytics.vue (non scopées là-bas), limitées ici au portail. */
#client-dashboard {
  .cp-road {
    padding: 10px 4px 12px;
    border-bottom: 1px solid #eee;
  }
  .ag-grid-cell-chips {
    top: 12px;
    transform: translateY(-50%);
    line-height: 1;
  }
}
</style>
